<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useUIStore } from "@/stores/ui";
import BaseIcon from "@/components/ui/BaseIcon.vue";
import logoSquareUrl from "@/assets/img/logo_sq.png";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const ui = useUIStore();

const menuItems = [
  { label: "概覽",     to: "/dashboard",   icon: "home" },
  { label: "頁面",     to: "/pages",       icon: "document-text" },
  { label: "活動",     to: "/events",      icon: "calendar" },
  { label: "媒體報導", to: "/media",       icon: "newspaper" },
  { label: "公眾查閱", to: "/documents",   icon: "folder-down" },
  { label: "聯絡訊息", to: "/submissions", icon: "envelope" },
  { label: "系統設定", to: "/settings",    icon: "cog" },
];

function isActive(to) {
  return route.path === to || route.path.startsWith(to + "/");
}

const showAccountDropdown = ref(false);
const accountDropdownRef = ref(null);

async function handleLogout() {
  showAccountDropdown.value = false;
  await auth.logout();
  router.push({ name: "login" });
}

function goToAccount() {
  showAccountDropdown.value = false;
  router.push({ name: "account" });
}

function handleClickOutside(e) {
  if (accountDropdownRef.value && !accountDropdownRef.value.contains(e.target)) {
    showAccountDropdown.value = false;
  }
}
onMounted(() => document.addEventListener("click", handleClickOutside));
onUnmounted(() => document.removeEventListener("click", handleClickOutside));
</script>

<template>
  <aside
    :class="[
      'fixed top-0 left-0 z-50 h-full w-[260px] bg-gradient-sidebar transition-[width,transform] duration-300 ease-smooth transform lg:translate-x-0',
      ui.sidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-[260px]',
      ui.sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full',
    ]"
  >
    <div class="relative h-full flex flex-col overflow-hidden">
      <!-- Logo + collapse toggle -->
      <div class="h-16 flex items-center justify-between px-4 border-b border-white/10 flex-shrink-0">
        <!-- Square emblem + system title (hidden when collapsed on desktop) -->
        <div :class="['flex items-center gap-2.5 overflow-hidden transition-all duration-300 ease-smooth', ui.sidebarCollapsed ? 'lg:w-0 lg:opacity-0' : 'w-auto opacity-100']">
          <img :src="logoSquareUrl" alt="香港青少年全人發展協會網站管理系統" class="h-9 w-9 flex-shrink-0 object-contain" />
          <div class="flex flex-col leading-tight min-w-0">
            <span class="text-xs font-semibold text-white whitespace-nowrap">香港青少年全人發展協會</span>
            <span class="text-[11px] text-white/55 whitespace-nowrap">網站管理系統</span>
          </div>
        </div>
        <!-- Collapsed (desktop): the square logo stays visible and doubles as the expand button -->
        <button
          v-if="ui.sidebarCollapsed"
          class="hidden lg:flex items-center justify-center w-10 h-10 mx-auto rounded-lg hover:bg-white/10 transition-all flex-shrink-0"
          title="展開側欄"
          @click="ui.toggleSidebar"
        >
          <img :src="logoSquareUrl" alt="香港青少年全人發展協會網站管理系統" class="h-8 w-8 object-contain" />
        </button>
        <!-- Expanded: collapse chevron -->
        <button
          v-else
          class="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors flex-shrink-0"
          title="收合側欄"
          @click="ui.toggleSidebar"
        >
          <BaseIcon name="chevron-left" class="w-5 h-5" />
        </button>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 py-4 px-3 overflow-x-hidden overflow-y-auto scrollbar-hide space-y-1">
        <RouterLink
          v-for="item in menuItems"
          :key="item.to"
          :to="item.to"
          :title="ui.sidebarCollapsed ? item.label : ''"
          class="group flex items-center rounded-lg transition-all duration-200 no-underline"
          :class="[
            ui.sidebarCollapsed ? 'justify-center px-3 py-2.5' : 'px-3 py-2.5 gap-3',
            isActive(item.to) ? 'nav-link-sidebar-active' : 'nav-link-sidebar',
          ]"
          @click="ui.closeMobileSidebar"
        >
          <BaseIcon :name="item.icon" class="w-5 h-5 flex-shrink-0 transition-transform duration-200" :class="isActive(item.to) ? '' : 'group-hover:scale-110'" />
          <span :class="['text-sm font-medium whitespace-nowrap transition-all duration-300 ease-smooth', ui.sidebarCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100']">
            {{ item.label }}
          </span>
        </RouterLink>
      </nav>

      <!-- Footer: account -->
      <div class="p-3 border-t border-white/10">
        <div ref="accountDropdownRef" class="relative">
          <button
            class="w-full flex items-center rounded-lg transition-all duration-300 ease-smooth text-white/70 hover:bg-white/10 hover:text-white"
            :class="ui.sidebarCollapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2'"
            @click="showAccountDropdown = !showAccountDropdown"
          >
            <BaseIcon name="user" class="w-5 h-5 flex-shrink-0" />
            <div :class="['flex-1 text-left min-w-0 overflow-hidden transition-all duration-300 ease-smooth', ui.sidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100']">
              <p class="text-xs font-medium text-white truncate">{{ auth.user?.email }}</p>
            </div>
            <BaseIcon name="chevron-down" :class="['w-4 h-4 text-white/50 flex-shrink-0 transition-all duration-300', showAccountDropdown ? 'rotate-180' : '', ui.sidebarCollapsed ? 'w-0 opacity-0' : 'opacity-100']" />
          </button>

          <Transition
            enter-active-class="transition duration-200 ease-smooth" enter-from-class="opacity-0 scale-95 translate-y-2"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0 scale-95 translate-y-2"
          >
            <div
              v-if="showAccountDropdown"
              :class="['absolute bottom-full mb-2 bg-slate-800 rounded-lg shadow-xl border border-white/10 overflow-hidden', ui.sidebarCollapsed ? 'left-full ml-2 w-48' : 'left-0 right-0']"
            >
              <button class="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors" @click="goToAccount">
                <BaseIcon name="lock" class="w-4 h-4" />
                <span>修改密碼</span>
              </button>
              <button class="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 transition-colors border-t border-white/10" @click="handleLogout">
                <BaseIcon name="logout" class="w-4 h-4" />
                <span>登出</span>
              </button>
            </div>
          </Transition>
        </div>

        <button
          class="lg:hidden w-full flex items-center justify-center gap-2 px-3 py-2 mt-2 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors"
          @click="ui.closeMobileSidebar"
        >
          <BaseIcon name="x" class="w-5 h-5" />
          <span class="text-xs font-medium">關閉選單</span>
        </button>

        <p
          v-if="!ui.sidebarCollapsed"
          class="mt-3 text-center text-[10px] leading-tight text-white/30 whitespace-nowrap"
        >
          HIMX 網站管理系統v1.0
        </p>
      </div>
    </div>
  </aside>
</template>
